'use client';

import { c as _c } from "react/compiler-runtime";
import './nav-account.scss';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu';
import { useTranslation } from 'react-i18next';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { AuthStatusEnum, useAuth } from '@sola/auth-sdk/store.tsx';
import { getPublicConfig } from '@sola/config/lib/getters.ts';
import { NameFormats, useSupporterName } from '@sola/supporter-sdk/hooks/details.ts';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { SupporterIdentity } from '../supporter-identity/supporter-identity.tsx';
type NavAccountProps = {
  userMenu?: React.ReactNode;
  urlLogin?: string;
  urlAccountHome?: string;
  onClose?: () => void;
  /** Render only a trigger button (used inside hamburger menu) */
  asTriggerOnly?: boolean;
  /** Called when trigger button is clicked (with asTriggerOnly) */
  onOpen?: () => void;
  /** Ref forwarded to the mobile takeover trigger for focus restoration */
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
};

// These hardcoded links will be used if no user menu is supplied from Contentful.
export const FallbackUserMenu = t0 => {
  const $ = _c(25);
  const {
    itemWrapper: t1
  } = t0;
  const itemWrapper = t1 === undefined ? DropdownMenuItem : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["priority", "core"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getPublicConfig();
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    NEXT_PUBLIC_BASE_URL_MYACCOUNT
  } = t3;
  const {
    actions: authActions
  } = useAuth("actions");
  const Wrapper = itemWrapper;
  let t4;
  if ($[2] !== authActions || $[3] !== t) {
    t4 = async () => {
      trackClick(t("priority:nav.logOut"));
      authActions.logOut();
    };
    $[2] = authActions;
    $[3] = t;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const logOut = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ArkIcon iconName="home-03" className="NavAccount__icon--small" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t) {
    t6 = t("core:actions.goToMyAccount");
    $[6] = t;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <span className="flex px-comfy-sm py-snug-xl gap-comfy-xs items-center">{t5}<LinkStyle noUnderline={true} noPadding={true} purpose="neutral"><Link href={NEXT_PUBLIC_BASE_URL_MYACCOUNT || "#"} className="NavAccount__menu-item w-full py-comfy-xs px-comfy-sm">{t6}</Link></LinkStyle></span>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== Wrapper || $[11] !== t7) {
    t8 = <Wrapper className="flex w-full align-start">{t7}</Wrapper>;
    $[10] = Wrapper;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ArkIcon iconName="log-out-01" className="NavAccount__icon--small" />;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t) {
    t10 = t("priority:nav.logOut");
    $[14] = t;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== logOut || $[17] !== t10) {
    t11 = <span className="flex px-comfy-sm py-snug-xl gap-comfy-xs items-center">{t9}<LinkStyle noUnderline={true} noPadding={true} purpose="negative"><button type="button" onClick={logOut}>{t10}</button></LinkStyle></span>;
    $[16] = logOut;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== Wrapper || $[20] !== t11) {
    t12 = <Wrapper className="flex w-full align-start">{t11}</Wrapper>;
    $[19] = Wrapper;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t8) {
    t13 = <>{t8}{t12}</>;
    $[22] = t12;
    $[23] = t8;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  return t13;
};
export const NavAccount: React.FC<NavAccountProps> = t0 => {
  const $ = _c(77);
  const {
    userMenu,
    urlLogin,
    urlAccountHome,
    onClose,
    asTriggerOnly,
    onOpen,
    triggerRef
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    currentStatus
  } = useAuth("currentStatus");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getPublicConfig();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    NEXT_PUBLIC_BASE_URL_MYACCOUNT
  } = t1;
  const {
    name: initials
  } = useSupporterName(NameFormats.initials);
  if (currentStatus !== AuthStatusEnum.Authenticated) {
    const t2 = urlLogin || NEXT_PUBLIC_BASE_URL_MYACCOUNT || "#";
    let t3;
    if ($[1] !== t) {
      t3 = t("priority:header.myAccount");
      $[1] = t;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] !== t) {
      t4 = t("priority:header.myAccount");
      $[3] = t;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <SupporterIdentity variant="loggedOut" />;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
      t6 = <Link type="button" href={t2} className="NavAccount__avatar NavAccount__avatar--loggedOut flex items-center justify-center py-snug-xl px-comfy-xs" title={t3} aria-label={t4}>{t5}</Link>;
      $[6] = t2;
      $[7] = t3;
      $[8] = t4;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  const isLegacySkippySolution = !userMenu;
  if (isLegacySkippySolution) {
    let t2;
    if ($[10] !== t) {
      t2 = t("priority:header.myAccount");
      $[10] = t;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    let t3;
    if ($[12] !== t) {
      t3 = t("priority:header.myAccount");
      $[12] = t;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    let t4;
    let t5;
    if ($[14] !== initials) {
      t4 = <SupporterIdentity variant="minimal" size="md" initials={initials} className="hidden lg:flex" />;
      t5 = <SupporterIdentity variant="dropdown" initials={initials} className="flex lg:hidden" />;
      $[14] = initials;
      $[15] = t4;
      $[16] = t5;
    } else {
      t4 = $[15];
      t5 = $[16];
    }
    let t6;
    if ($[17] !== t2 || $[18] !== t3 || $[19] !== t4 || $[20] !== t5) {
      t6 = <DropdownMenuTrigger asChild={true}><button type="button" className="NavAccount__avatar NavAccount__avatar--dropdown flex p-comfy-xs" title={t2} aria-label={t3}>{t4}{t5}</button></DropdownMenuTrigger>;
      $[17] = t2;
      $[18] = t3;
      $[19] = t4;
      $[20] = t5;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    let t7;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <DropdownMenuContent align="end" className="NavAccount__menu flex flex-col items-end mt-snug-lg py-snug-xl"><FallbackUserMenu /></DropdownMenuContent>;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    let t8;
    if ($[23] !== t6) {
      t8 = <DropdownMenu>{t6}{t7}</DropdownMenu>;
      $[23] = t6;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    return t8;
  }
  if (asTriggerOnly) {
    let t2;
    if ($[25] !== t) {
      t2 = t("priority:header.myAccount");
      $[25] = t;
      $[26] = t2;
    } else {
      t2 = $[26];
    }
    let t3;
    if ($[27] !== t) {
      t3 = t("priority:header.myAccount");
      $[27] = t;
      $[28] = t3;
    } else {
      t3 = $[28];
    }
    let t4;
    if ($[29] !== initials) {
      t4 = <SupporterIdentity variant="dropdown" initials={initials} />;
      $[29] = initials;
      $[30] = t4;
    } else {
      t4 = $[30];
    }
    let t5;
    if ($[31] !== onOpen || $[32] !== t2 || $[33] !== t3 || $[34] !== t4) {
      t5 = <button type="button" onClick={onOpen} className="NavAccount__avatar NavAccount__avatar--dropdown flex items-center justify-center" title={t2} aria-label={t3}>{t4}</button>;
      $[31] = onOpen;
      $[32] = t2;
      $[33] = t3;
      $[34] = t4;
      $[35] = t5;
    } else {
      t5 = $[35];
    }
    return t5;
  }
  const t2 = urlAccountHome || NEXT_PUBLIC_BASE_URL_MYACCOUNT || "#";
  let t3;
  if ($[36] !== t) {
    t3 = t("priority:header.myAccount");
    $[36] = t;
    $[37] = t3;
  } else {
    t3 = $[37];
  }
  let t4;
  if ($[38] !== t) {
    t4 = t("priority:header.myAccount");
    $[38] = t;
    $[39] = t4;
  } else {
    t4 = $[39];
  }
  let t5;
  if ($[40] !== initials) {
    t5 = <SupporterIdentity variant="minimal" initials={initials} size="md" />;
    $[40] = initials;
    $[41] = t5;
  } else {
    t5 = $[41];
  }
  let t6;
  if ($[42] !== t2 || $[43] !== t3 || $[44] !== t4 || $[45] !== t5) {
    t6 = <Link type="button" href={t2} className="NavAccount__avatar hidden lg:flex items-center justify-center no-underline" title={t3} aria-label={t4}>{t5}</Link>;
    $[42] = t2;
    $[43] = t3;
    $[44] = t4;
    $[45] = t5;
    $[46] = t6;
  } else {
    t6 = $[46];
  }
  let t7;
  if ($[47] !== t) {
    t7 = t("priority:header.myAccount");
    $[47] = t;
    $[48] = t7;
  } else {
    t7 = $[48];
  }
  let t8;
  if ($[49] !== t) {
    t8 = t("priority:header.myAccount");
    $[49] = t;
    $[50] = t8;
  } else {
    t8 = $[50];
  }
  let t9;
  if ($[51] !== initials) {
    t9 = <SupporterIdentity variant="dropdown" initials={initials} />;
    $[51] = initials;
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  let t10;
  if ($[53] !== t7 || $[54] !== t8 || $[55] !== t9 || $[56] !== triggerRef) {
    t10 = <NavigationMenu.Trigger ref={triggerRef} onPointerEnter={_temp} onPointerMove={_temp2} onPointerLeave={_temp3} className="NavAccount__avatar NavAccount__avatar--dropdown flex items-center justify-center" title={t7} aria-label={t8}>{t9}</NavigationMenu.Trigger>;
    $[53] = t7;
    $[54] = t8;
    $[55] = t9;
    $[56] = triggerRef;
    $[57] = t10;
  } else {
    t10 = $[57];
  }
  let t11;
  if ($[58] !== t) {
    t11 = t("priority:nav.close", {
      defaultValue: "Close"
    });
    $[58] = t;
    $[59] = t11;
  } else {
    t11 = $[59];
  }
  let t12;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ArkIcon iconName="x-close" className="NavAccount__icon" />;
    $[60] = t12;
  } else {
    t12 = $[60];
  }
  let t13;
  if ($[61] !== onClose || $[62] !== t11) {
    t13 = <div className="NavAccount__takeover-header flex items-center justify-end px-snug-md py-comfy-sm"><button type="button" className="NavAccount__takeover-close flex items-center p-comfy-xs" aria-label={t11} onClick={onClose}>{t12}</button></div>;
    $[61] = onClose;
    $[62] = t11;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  let t14;
  if ($[64] !== userMenu) {
    t14 = userMenu || <FallbackUserMenu itemWrapper="div" />;
    $[64] = userMenu;
    $[65] = t14;
  } else {
    t14 = $[65];
  }
  let t15;
  if ($[66] !== t14) {
    t15 = <div className="NavAccount__takeover-content px-comfy-sm pt-snug-xl">{t14}</div>;
    $[66] = t14;
    $[67] = t15;
  } else {
    t15 = $[67];
  }
  let t16;
  if ($[68] !== t13 || $[69] !== t15) {
    t16 = <NavigationMenu.Content className="Header__content Header__content--mobile flex flex-col" onPointerLeave={_temp4}><div className="NavAccount__takeover flex flex-col">{t13}{t15}</div></NavigationMenu.Content>;
    $[68] = t13;
    $[69] = t15;
    $[70] = t16;
  } else {
    t16 = $[70];
  }
  let t17;
  if ($[71] !== t10 || $[72] !== t16) {
    t17 = <NavigationMenu.Item className="flex lg:hidden items-center" value="account" asChild={true}><div>{t10}{t16}</div></NavigationMenu.Item>;
    $[71] = t10;
    $[72] = t16;
    $[73] = t17;
  } else {
    t17 = $[73];
  }
  let t18;
  if ($[74] !== t17 || $[75] !== t6) {
    t18 = <>{t6}{t17}</>;
    $[74] = t17;
    $[75] = t6;
    $[76] = t18;
  } else {
    t18 = $[76];
  }
  return t18;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.preventDefault();
}
function _temp3(e_1) {
  return e_1.preventDefault();
}
function _temp4(e_2) {
  return e_2.preventDefault();
}